/**
 * Event bus ligero: misma pestaña, sin WebSocket/SSE. Invalidación explícita por dominio.
 *
 * ─── Regla general ───
 * - Fuente de verdad: REST + estado en React (App/HomePage para facturas/presupuestos globales).
 * - Tras mutación que deba verse en otro módulo abierto: emitir el dominio correspondiente.
 * - Quien escucha: refetch o setState local; no hay cache automático salvo TTL en appDataStore / notificaciones.
 *
 * ─── Inventario fase 2 (emit / on) ───
 *
 * ventas
 *   Fuente de verdad lista facturas: App.facturas (HomePage hace listFacturas al emitir).
 *   emit: VentasModule (crear / confirmar / eliminar factura, NC).
 *   on: HomePage (refreshFacturas en appDataStore + emit notifications);
 *   FacturaForm y ClientesModule (saldo); CajaBancosModule (cierre abierto).
 *   Nota: VentasModule copia siempre facturasCache del padre (incluye []).
 *
 * clientes
 *   emit: ClientesABM (create/update/delete, incl. modal createOnly en Ventas);
 *   VentasModule, FacturacionList (post-confirmar), RecibosModule.
 *   on: ClientesModule useDataRefresh (listado Estado de cuentas, silent);
 *   ClienteEstadoCuenta useDataRefresh junto con facturas.
 *
 * facturas (señal secundaria, no reemplaza ventas para el cache global)
 *   emit: FacturacionList tras confirmar CAE/FX (además de ventas vía padre).
 *   on: ClienteEstadoCuenta useDataRefresh(['clientes','facturas']).
 *
 * presupuestos
 *   Fuente de verdad: App.presupuestos.
 *   emit: VentasModule al crear factura (estado presupuesto).
 *   on: HomePage listPresupuestos.
 *
 * remitos (venta)
 *   emit: VentasModule al confirmar factura (auto-remito), al eliminar y en NC.
 *   on: RemitosVtasModule listener dataRefresh.on('remitos').
 *
 * stock
 *   emit: VentasModule (NC), StockABM, StockImport, Navbar (acciones masivas CSV), ProveedoresRemitos (finalizar remito proveedor, E.4), RemitosVtasModule (marcar parcial / finalizar — descuenta stock).
 *   on: App (refresh notificationsLiveStore), StockModule (incrementa busRefreshToken → StockListado refetch, incluso con keep-alive u otra sub-vista), StockABM (refetch ubicaciones del producto abierto, stale-while-revalidate).
 *
 * depositos
 *   emit: DepositosABM (create/update/delete en Opciones); SucursalesABM (M2M depósitos al crear/editar).
 *   on: StockABM (refetch catálogo sucursales/depósitos para el dropdown de ubicaciones, sin F5).
 *
 * notifications
 *   emit: StockABM, StockImport, Navbar.
 *   on: App (refresh notificationsLiveStore usando appDataStore como fuente de facturas).
 *
 * userAvatar
 *   emit: PerfilPage, UsuariosABM tras upload exitoso de avatar.
 *   on: Navbar (refetch getAvatar para el username de sesión).
 *
 * proveedores
 *   emit: ProveedoresABM (create/update/delete); ProveedoresPagos tras pago.
 *   refetch directo: ProveedoresPedidos llama onAfterPedidoSuccess={fetchProveedores} (evita perder el listener si el array de eventos no es estable).
 *   on: ProveedoresModule useDataRefresh(proveedoresRefreshEvents, fetchProveedores).
 *
 * proveedores-pedidos
 *   emit: ProveedoresRemitos tras finalizar remito (pedido pasa a recibido + fecha_finalizado en backend).
 *   on: ProveedoresPedidos useDataRefresh → refetch listado sin F5.
 *
 * caja (movimientos + cierres globales en App) — fase 3
 *   Fuente de verdad: estado elevado en App (movimientos, cierres), reflejado en appDataStore tras setState.
 *   Tras cada fetch exitoso en CajaBancosModule.fetchData se invocan onMovimientosUpdate / onCierresUpdate
 *   (HomePage → setMovimientos / setCierres).
 *   Mutaciones de caja fuera del módulo: FondosModule emite `cierres` (y `fondos`) al borrar un fondo
 *   para que el cierre abierto keep-alive se refresque sin «Actualizar Datos».
 *
 * movimientos | cierres (como RefreshEvent)
 *   cierres: emit FondosModule tras DELETE; on CajaBancosModule (refetch silent, even if hidden).
 *   movimientos: reservado; no confundir con el dominio "caja" anterior.
 *
 * cheques
 *   emit: VentasModule tras confirmar factura si hay método cheque; ProveedoresPagos tras pago con cheque nuevo;
 *   RecibosModule tras crear/actualizar recibo con alta de cheque nuevo (no solo referencia a existente).
 *   on: ChequesModule useDataRefresh → refetch silencioso (mismos filtros/página) sin depender de F5.
 *
 * recibos
 *   emit: CierreDiarioListado al cambiar el método de un cobro REC.
 *   on: RecibosModule refetch silencioso aunque el módulo esté oculto (keep-alive). Sin eso, el detalle sigue en efectivo hasta F5.
 *
 * fondos
 *   emit: NuevoMovimiento (Caja) tras createMovimiento exitoso — el BE puede alta automática de Fondo
 *   si el tipo tiene "Registrar en Fondos"; FondosModule tras DELETE (además de `cierres`).
 *   on: FondosModule useDataRefresh → refetch listado + balance ARS/USD (keep-alive; sin F5);
 *   CajaBancosModule (junto con `cierres`) para alinear el cierre abierto.
 *   Además: FondosModule refetch al reactivar isActive (TTL) y al focusRefreshToken de HomePage.
 *
 * all
 *   Listeners de 'all' reciben cualquier emit. Reservado para usos puntuales.
 *
 * ─── Huérfanos evitados ───
 * - proveedores: antes solo había listener (ProveedoresModule) sin emit; corregido en ProveedoresABM.
 * - clientes: mismo hueco en ClientesABM (F0); emit create/update/delete.
 * - remitos: tenía listener (RemitosVtas) y emit (Ventas); consistente.
 */
type RefreshHandler = () => void;
export type RefreshEvent =
  | 'clientes'
  | 'facturas'
  | 'remitos'
  | 'presupuestos'
  | 'proveedores'
  | 'proveedores-pedidos'
  | 'cheques'
  | 'fondos'
  | 'ventas'
  | 'stock'
  | 'depositos'
  | 'notifications'
  | 'movimientos'
  | 'cierres'
  | 'recibos'
  /** Avatar del usuario en sesión (p. ej. tras upload en Perfil u Opciones). */
  | 'userAvatar'
  | 'all';

class DataRefreshEmitter {
    private listeners: Map<RefreshEvent, Set<RefreshHandler>> = new Map();

    on(event: RefreshEvent, handler: RefreshHandler) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(handler);
    }

    off(event: RefreshEvent, handler: RefreshHandler) {
        this.listeners.get(event)?.delete(handler);
    }

    emit(event: RefreshEvent) {
        // console.log(`[DataRefresh] Emitting refresh for: ${event}`);

        // Emit specific event
        this.listeners.get(event)?.forEach(handler => {
            try {
                handler();
            } catch (e) {
                console.error(`[DataRefresh] Error in handler for ${event}:`, e);
            }
        });

        // Also emit 'all' listeners for any event
        if (event !== 'all') {
            this.listeners.get('all')?.forEach(handler => {
                try {
                    handler();
                } catch (e) {
                    console.error(`[DataRefresh] Error in 'all' handler:`, e);
                }
            });
        }
    }
}

export const dataRefresh = new DataRefreshEmitter();

// Hook for easy use in components
import { useEffect } from 'react';

export function useDataRefresh(events: RefreshEvent[], onRefresh: RefreshHandler) {
    useEffect(() => {
        events.forEach(event => dataRefresh.on(event, onRefresh));
        return () => {
            events.forEach(event => dataRefresh.off(event, onRefresh));
        };
    }, [events, onRefresh]);
}
